import { capitalize } from "lodash";
import { useRef, useState } from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import {
  AdvancedDataGrid,
  Avatar,
  createDataCardInitialColumnDefinition,
  createInfoCardColumnDefinition,
  DataGridRef,
  DateDisplay,
  formatDate,
  NestedDataCardProps,
  SelectedFilters,
  SingleStatusChip,
} from "@inductiveautomation/ws-components";

import { LicenseSession } from "../../Licenses.types";
import InvalidateSession from "../dialogs/InvalidateSession";
import SessionsDrawer from "./Sessions.Drawer";
import { getLicenseStyles } from "../_licenseStyle";
import { MAX_MAKER_LICENSE_SESSIONS_COUNT } from "../../Licenses.data";
import { matchesDateRangeFilter, matchesStringArrayFilter } from "../utilities";
import { useDemo } from "../../mocks/useDemo";

const Sessions = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { classes } = getLicenseStyles();

  const { mockDataForLicense } = useDemo();
  const license = mockDataForLicense(id ?? "maker-license-1");

  const dataGridRef = useRef<DataGridRef>(null);
  const [activeSession, setActiveSession] = useState<LicenseSession>();
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>();
  const [sessionToInvalidate, setSessionToInvalidate] =
    useState<LicenseSession>();

  const invalidateSession = (sessionIpAddress: string) => {
    const session = license.sessions.find(
      (session) => session.ipAddress === sessionIpAddress
    );
    setSessionToInvalidate(session);
  };

  const sessionStatuses = license.sessions.reduce<string[]>(
    (statuses, session) => {
      const lowercaseStatus = session.status.toLowerCase();

      return statuses.includes(lowercaseStatus)
        ? statuses
        : [...statuses, lowercaseStatus];
    },
    []
  );

  const filteredData = license.sessions
    .filter((session) => {
      const validatedMatches = matchesDateRangeFilter(
        selectedFilters?.["filter-validated"],
        session.validated
      );
      const invalidatedMatches = matchesDateRangeFilter(
        selectedFilters?.["filter-invalidated"],
        session.invalidated
      );
      const checkInMatches = matchesDateRangeFilter(
        selectedFilters?.["filter-check_in"],
        session.nextCheckIn
      );
      const expirationMatches = matchesDateRangeFilter(
        selectedFilters?.["filter-expiration"],
        session.expiration
      );
      const statusMatches = matchesStringArrayFilter(
        selectedFilters?.["filter-status"],
        `status-${session.status.toLowerCase()}`
      );

      return (
        validatedMatches &&
        invalidatedMatches &&
        checkInMatches &&
        expirationMatches &&
        statusMatches
      );
    })
    .map(sessionToDataCardMapper(t, invalidateSession));

  return (
    <div className={classes.dataGridContainer}>
      <AdvancedDataGrid
        ref={dataGridRef}
        multiSelect={false}
        chromeless
        testId="maker-license-sessions"
        headings={[
          {
            id: "maker-license-sessions-heading-details",
            label: t("maker_license.tabs.sessions.data_grid.headings.details"),
          },
          {
            id: "maker-license-sessions-heading-validated",
            label: t(
              "maker_license.tabs.sessions.data_grid.headings.validated"
            ),
          },
          {
            id: "maker-license-sessions-heading-invalidated",
            label: t(
              "maker_license.tabs.sessions.data_grid.headings.invalidated"
            ),
          },
          {
            id: "maker-license-sessions-heading-check_in",
            label: t("maker_license.tabs.sessions.data_grid.headings.check_in"),
          },
          {
            id: "maker-license-sessions-heading-expiration",
            label: t(
              "maker_license.tabs.sessions.data_grid.headings.expiration"
            ),
          },
          {
            id: "maker-license-sessions-heading-status",
            label: t("maker_license.tabs.sessions.data_grid.headings.status"),
            alignment: "right",
          },
        ]}
        data={filteredData}
        filterChips={[
          {
            id: "filter-validated",
            label: t("maker_license.tabs.sessions.data_grid.filters.validated"),
            variant: "date_range_picker",
          },
          {
            id: "filter-invalidated",
            label: t(
              "maker_license.tabs.sessions.data_grid.filters.invalidated"
            ),
            variant: "date_range_picker",
          },
          {
            id: "filter-check_in",
            label: t("maker_license.tabs.sessions.data_grid.filters.check_in"),
            variant: "date_range_picker",
          },
          {
            id: "filter-expiration",
            label: t(
              "maker_license.tabs.sessions.data_grid.filters.expiration"
            ),
            variant: "date_range_picker",
          },
          {
            id: "filter-status",
            label: t("maker_license.tabs.sessions.data_grid.filters.status"),
            variant: "list_advanced",
            listItems: [
              sessionStatuses.map((status) => ({
                id: `status-${status}`,
                label: capitalize(status),
                variant: "checkbox",
              })),
            ],
          },
        ]}
        onSelection={(rowId) => {
          const session = license.sessions.find(
            (session) => session.id === rowId
          );
          setActiveSession(session);
        }}
        selectedFilters={selectedFilters}
        onFiltersUpdated={setSelectedFilters}
        generateCsvData={() => ({
          headings: [
            t("maker_license.tabs.sessions.data_grid.fields.name"),
            t("maker_license.tabs.sessions.data_grid.fields.ip_address"),
            t("maker_license.tabs.sessions.data_grid.headings.validated"),
            t("maker_license.tabs.sessions.data_grid.headings.invalidated"),
            t("maker_license.tabs.sessions.data_grid.headings.check_in"),
            t("maker_license.tabs.sessions.data_grid.headings.expiration"),
            t("maker_license.tabs.sessions.data_grid.headings.status"),
          ],
          rows: license.sessions.map((session) => [
            session.name,
            session.ipAddress,
            formatDate(session.validated, "date_and_time"),
            formatDate(session.invalidated, "date_and_time"),
            formatDate(session.nextCheckIn, "date_and_time"),
            formatDate(session.expiration, "date_and_time"),
            "",
          ]),
        })}
        labels={{
          singular_item_name: t(
            "maker_license.tabs.sessions.data_grid.singular_item_name"
          ),
          plural_item_name: t(
            "maker_license.tabs.sessions.data_grid.plural_item_name"
          ),
        }}
        footerProps={{
          helperText: t(
            "maker_license.tabs.sessions.data_grid.sessions_available",
            {
              count: MAX_MAKER_LICENSE_SESSIONS_COUNT - license.sessions.length,
            }
          ),
        }}
      />

      <InvalidateSession
        session={sessionToInvalidate}
        license={license}
        onClose={() => setSessionToInvalidate(undefined)}
        onSubmit={() => setSessionToInvalidate(undefined)}
      />

      <SessionsDrawer
        session={activeSession}
        onClose={() => {
          setActiveSession(undefined);
          dataGridRef.current?.clearCheckedAndSelected();
        }}
        invalidateSession={(session) => setSessionToInvalidate(session)}
      />
    </div>
  );
};

export default Sessions;

const sessionToDataCardMapper =
  (t: TFunction, invalidateSession: (sessionId: string) => void) =>
  (session: LicenseSession): NestedDataCardProps => {
    return {
      id: session.id,
      variant: "default",
      contentColumns: [
        createDataCardInitialColumnDefinition({
          parentTestId: session.id,
          avatar: <Avatar icon="session" iconAvatarColor="primary" />,
          heading: session.name,
          subtext: session.ipAddress,
        }),
        { content: <DateDisplay date={session.validated} /> },
        { content: <DateDisplay date={session.invalidated} /> },
        { content: <DateDisplay date={session.nextCheckIn} /> },
        { content: <DateDisplay date={session.expiration} /> },
        createInfoCardColumnDefinition(
          <SingleStatusChip
            testId="session-status-chip"
            label={capitalize(session.status.toLowerCase())}
          />
        ),
      ],
      actionLists: [
        session.status !== "INVALID"
          ? [
              {
                id: "invalidate",
                label: t(
                  "maker_license.tabs.sessions.actions.invalidate.label"
                ),
                iconLeft: "license-invalidate",
                iconLeftVariant: "destructive",
                onClick: () => invalidateSession(session.ipAddress),
              },
            ]
          : [],
      ],
      childItems: [],
    };
  };
